import {
    CustomCheckbox,
    CustomCollapseCard,
    CustomImage,
    CustomInput,
    successDialog,
    Text,
    useText,
} from '@src/components';
import { Button, Modal, Radio } from 'antd';
import modalClose from '@src/assets/icons/icon-close.svg';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    cardOrdersSave,
    cardOrdersUpdate,
    setCurrentCardOrder,
} from '@src/store/slice/cardOrderSlice';
import { customerLogosGetList } from '@src/store/slice/customerSlice';
import { VouIdCode } from '@src/store/enums';
import PropTypes from 'prop-types';

const CardParameter = ({ vouId = VouIdCode.Market }) => {
    const { currentCardOrder } = useSelector((state) => state?.cardOrder);
    const dispatch = useDispatch();

    const [companyLogos, setCompanyLogos] = useState([]);
    const [selectedIcon, setSelectedIcon] = useState(0);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [companyInput, setCompanyInput] = useState('');
    const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');

    useEffect(() => {
        (async () => {
            const action = await dispatch(customerLogosGetList({ vouId }));
            if (customerLogosGetList.fulfilled.match(action)) {
                setCompanyLogos(action?.payload?.data);
            }
        })();
    }, [dispatch, vouId]);

    useEffect(() => {
        setCompanyInput(currentCardOrder?.companyName);
    }, [currentCardOrder?.companyName]);

    const cardParameterInputChange = useCallback(
        async (name, value) => {
            const { id, cardOrderDetails, ...cardOrderBody } = currentCardOrder;
            if (id === 0) {
                if (name === 'printCompanyLogo' && value === true && selectedIcon) {
                    await dispatch(cardOrdersSave({ ...cardOrderBody, logoId: selectedIcon, [name]: value }));
                } else if (name === 'printCompanyLogo' && value === false) {
                    await dispatch(cardOrdersSave({ ...cardOrderBody, logoId: 0, [name]: value }));
                } else if (name === 'printCompanyLogo' && value === true && selectedIcon === 0) {
                    await dispatch(
                        cardOrdersSave({ ...cardOrderBody, logoId: 0, printCompanyLogo: false, [name]: value }),
                    );
                } else {
                    await dispatch(cardOrdersSave({ ...cardOrderBody, [name]: value }));
                }
            } else if (name === 'printCompanyLogo' && value === true && selectedIcon) {
                await dispatch(
                    cardOrdersSave({ ...cardOrderBody, logoId: selectedIcon, [name]: value, orderStatus: 0 }),
                );
            } else if (name === 'printCompanyLogo' && value === false) {
                await dispatch(
                    cardOrdersSave({ ...cardOrderBody, logoId: 0, [name]: value, orderStatus: 0 }),
                );
            } else if (name === 'printCompanyLogo' && value === true && selectedIcon === 0) {
                await dispatch(
                    cardOrdersSave({ ...cardOrderBody, logoId: 0, printCompanyLogo: false, orderStatus: 0 }),
                );
            } else {
                await dispatch(cardOrdersSave({ ...cardOrderBody, [name]: value, orderStatus: 0 }));
            }
        },
        [currentCardOrder, dispatch, selectedIcon],
    );

    const handleOrderSave = useCallback(async () => {
        const { id, cardOrderDetails, ...cardOrderBody } = currentCardOrder;
        if (id === 0) {
            const action = await dispatch(
                cardOrdersSave({ ...cardOrderBody, companyName: companyInput }),
            );
            if (cardOrdersSave.fulfilled.match(action)) {
                successDialog({
                    title: <Text t="successfullySent" />,
                    message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
                });
            }
        } else {
            const action = await dispatch(
                cardOrdersUpdate({
                    id,
                    ...cardOrderBody,
                    companyName: companyInput,
                    orderStatus: 0,
                }),
            );
            if (cardOrdersUpdate.fulfilled.match(action)) {
                successDialog({
                    title: <Text t="successfullySent" />,
                    message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
                });
            }
        }
    }, [currentCardOrder, dispatch, companyInput]);

    const onChange = useCallback((e) => {
        setSelectedIcon(e.target.value);
    }, []);

    const showModal = useCallback(() => {
        setIsModalVisible(true);
    }, []);

    const clickOkButton = useCallback(async () => {
        setIsModalVisible(false);
        dispatch(setCurrentCardOrder({ logoId: selectedIcon, printCompanyLogo: true }));
        const { id, cardOrderDetails, ...cardOrderBody } = currentCardOrder;
        if (id === 0) {
            await dispatch(
                cardOrdersSave({ ...cardOrderBody, logoId: selectedIcon, printCompanyLogo: true }),
            );
        } else {
            await dispatch(
                cardOrdersUpdate({ id, ...cardOrderBody, logoId: selectedIcon, printCompanyLogo: true }),
            );
        }
    }, [dispatch, selectedIcon, currentCardOrder]);

    const clickCancelButton = useCallback(async () => {
        dispatch(setCurrentCardOrder({ logoId: 0 }));
        const { id, cardOrderDetails, ...cardOrderBody } = currentCardOrder;
        if (id === 0) {
            await dispatch(cardOrdersSave({ ...cardOrderBody, logoId: 0 }));
        } else {
            await dispatch(cardOrdersUpdate({ id, ...cardOrderBody, logoId: 0 }));
        }
        setSelectedIcon(0);
        setIsModalVisible(false);
    }, [dispatch, currentCardOrder]);

    return (
        <CustomCollapseCard
            bodyCenter
            cardTitle={<Text t="cardParameterTitle" />}
            cardTitleAlign="center"
            titleColor="#1d2228"
            titleSize="18px"
            className="complimet-card-order-pyh-collapse "
            showExpandIcon={false}
        >
            <div className="card-parameter-container">
                <div className="card-parameter-description">
                    <p>
                        <Text t="inYourNewCardOrdersYouCanChoose" />
                    </p>
                    <div className="card-parameters-form">
                        <div className="parameters-form-item">
                            <label className="parameters-form-item-label">
                                <CustomCheckbox
                                    id="ticketRestaurant-cardOrders-cardInformation-cardParameter-printNameSurname-checkbox"
                                    checked={currentCardOrder?.printNameSurname}
                                    className="compliment-custom-checbox"
                                    onChange={(e) => cardParameterInputChange('printNameSurname', e?.target?.checked)}
                                />
                                <div style={{ marginRight: 0, marginLeft: 8, marginTop: 8 }}>
                                    <Text t="nameSurname" />
                                </div>
                            </label>
                        </div>
                        <div className="parameters-form-item">
                            <label className="parameters-form-item-label">
                                <CustomCheckbox
                                    id="ticketRestaurant-cardOrders-cardInformation-cardParameter-printCompanyName-checkbox"
                                    checked={currentCardOrder?.printCompanyName}
                                    className="compliment-custom-checbox"
                                    onChange={(e) => cardParameterInputChange('printCompanyName', e?.target?.checked)}
                                />
                                <div style={{ marginRight: 0, marginLeft: 8, marginTop: 8 }}>
                                    <Text t="companyName" />
                                </div>
                            </label>
                            {currentCardOrder?.printCompanyName && (
                                <div className="compliment-parameter-input-contain">
                                    <CustomInput
                                        id="ticketRestaurant-cardOrders-cardInformation-cardParameter-cardParameterSave-input"
                                        value={companyInput}
                                        onChange={(e) => setCompanyInput(e?.target?.value)}
                                    />
                                    <button
                                        style={{ backgroundColor: 'transparent', borderWidth: 0 }}
                                        onClick={handleOrderSave}
                                    >
                                        <Text t="cardParameterSave" />
                                    </button>
                                </div>
                            )}
                        </div>

                        {companyLogos.length > 0 && (
                            <div className="parameters-form-item">
                                <label className="parameters-form-item-label">
                                    <CustomCheckbox
                                        id="ticketRestaurant-cardOrders-cardInformation-cardParameter-printCompanyLogo-checkbox"
                                        checked={currentCardOrder?.printCompanyLogo}
                                        onChange={(e) => {
                                            const val = e?.target?.checked;
                                            if (val) {
                                                setIsModalVisible(true);
                                            }

                                            cardParameterInputChange('printCompanyLogo', val);
                                        }}
                                        className="compliment-custom-checbox"
                                    />
                                    <div style={{ marginRight: 0, marginLeft: 8, marginTop: 8 }}>
                                        <Text t="companyLogo" />
                                    </div>
                                </label>

                                <div className="compliment-parameter-input-contain">
                                    <button
                                        style={{ backgroundColor: 'transparent', borderWidth: 0 }}
                                        onClick={showModal}
                                    >
                                        <Text t="cardParameterChooseLogo" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="compliment-absolute-card-container">
                    <div className="market-absolute-card">
                        {currentCardOrder?.logoId && companyLogos?.length > 0 ? (
                            <div className="compliment-absolute-card-company-logo">
                                <CustomImage
                                    style={{ height: 40, objectFit: 'contain' }}
                                    src={companyLogos.filter((item) => item.id === currentCardOrder?.logoId)[0].logo}
                                />
                            </div>
                        ) : null}
                        {currentCardOrder?.printCompanyName ? (
                            <div className="compliment-absolute-card-company-name">{companyInput}</div>
                        ) : null}
                        {currentCardOrder?.printNameSurname ? (
                            <div className="compliment-absolute-card-name"><Text t="name" /></div>
                        ) : null}
                        {currentCardOrder?.printNameSurname ? (
                            <div className="compliment-absolute-card-surname"><Text t="surnameUpperCase" /></div>
                        ) : null}
                        <div className="compliment-absolute-card-name-2">61378XXXXXXXXX</div>
                    </div>
                </div>
            </div>

            {companyLogos.length > 0 && (
                <Modal
                    className="card-parameter-modal"
                    width="639px"
                    footer={false}
                    title={<Text t="cardParameterChooseLogo" />}
                    visible={isModalVisible}
                    onOk={clickOkButton}
                    onCancel={clickCancelButton}
                    closeIcon={<CustomImage src={modalClose} />}
                >
                    <p>
                        <Text t="cardParameterChooseLogoDesc" />
                    </p>
                    <Radio.Group
                        id="ticketRestaurant-cardOrders-cardInformation-cardParameter-custom-radioGroup-input-content"
                        onChange={onChange}
                        value={selectedIcon}
                        className="custom-radio-input-content"
                    >
                        {companyLogos.map((logo) => (
                            <Radio
                                id="ticketRestaurant-cardOrders-cardInformation-cardParameter-custom-radio-input-content"
                                key={logo?.id}
                                value={logo?.id}
                                className="custom-radio-input"
                            >
                                <CustomImage src={logo?.logo} />
                            </Radio>
                        ))}
                    </Radio.Group>
                    <div className="custom-parameter-modal-footer">
                        <button
                            className="custom-btn-cancel"
                            style={{ backgroundColor: 'transparent', borderWidth: 0 }}
                            onClick={clickCancelButton}
                        >
                            <Text t="giveUp" />
                        </button>
                        <Button
                            id="cardOrders-cardInformation-cardParameter-custom-btn-primary"
                            disabled={!selectedIcon}
                            className="custom-btn-primary"
                            onClick={clickOkButton}
                        >
                            <Text t="okBtn" />
                        </Button>
                    </div>
                </Modal>
            )}
        </CustomCollapseCard>
    );
};

CardParameter.propTypes = {
    vouId: PropTypes.oneOf([VouIdCode.Market, VouIdCode.Compliments]),
};

export default CardParameter;
